import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  ExternalUserConfigResponse,
  LocaleItem,
  LocalesResponse,
  MetaDataResponse,
  ResultValidationResponse,
} from '../../@types/common';
import type { FeatureTourResponse, FeatureTours } from '../../@types/feature-tours';
import type {
  FormAccessControlResponse,
  FormAccessControlResult,
  FormPermissionsResponse,
  FormPermissionsResults,
} from '../../@types/form-permissions';
import type {
  DynamicFormSettings,
  DynamicFormSettingsWithOptionalId,
  DynamicFormSettingsWrapper,
  ExtendedDynamicFormSettings,
  ExtendedDynamicFormWorkflowStep,
  GetWorkflowStepsResponse,
  PostFormByReferenceIdApiRequest,
  PostFormByReferenceIdApiResponse,
  WorkplaceSignInFormResponse,
} from '../../@types/forms';
import type { HelpMetadata, HomePageSummary, HomePageSummaryResponse } from '../../@types/home-page-data';
import type { LicencesInfo, LicencesWrapper } from '../../@types/licences';
import type {
  FormWorkflowStepsApiProps,
  Module,
  ModuleApiProps,
  ModuleForms,
  ModuleGroups,
  ModuleWorkflowStepsReponse,
} from '../../@types/modules';
import type { NavigationItem, NavigationItemsApiResponse } from '../../@types/navigation';
import type { NewsResult } from '../../@types/news';
import type { SchemaDetails, SchemaDetailsResponse, SchemaInfo } from '../../@types/schema';
import type {
  NewsApiProps,
  NewsEntryProps,
  NewsItemDismissed,
  NewsItemSeen,
} from '../../components/home-page/news-entry.component';
import { isOfflineEnabled } from '../../db/odin-db-util';
import { transformModuleForms, transformNewsEntries } from '../../utils/data-transformation-utils';
import { processWorkflowSteps, settingsToDynamicForm } from '../../utils/dynamic-form-utils';
import {
  createFieldFilterText,
  createFieldFilterTextAsFirstQueryParameter,
} from '../../utils/filter-url-builder-utils';
import { offlineAwareBaseQuery } from '../util/offline-api-util';
import { isWebView, webViewBaseQuery } from '../util/webview-base-query';

export const api = createApi({
  keepUnusedDataFor: isOfflineEnabled ? 3600 * 1 /* 1 hour */ : 60 /* 60 seconds */,
  baseQuery: isWebView() ? webViewBaseQuery : offlineAwareBaseQuery,
  tagTypes: [
    'Theme',
    'News',
    'Licenses',
    'FeatureTours',
    'FormConfig',
    'ModuleForm',
    'HelpMetadata',
    'NavigationItems',
  ],
  endpoints: (builder) => ({
    moduleWorkflowSteps: builder.query<Array<string>, ModuleApiProps>({
      query: ({ moduleId, filters }) =>
        `/modules/${moduleId}/workflow-steps${createFieldFilterTextAsFirstQueryParameter(filters, true)}`,
      transformResponse: (response: ModuleWorkflowStepsReponse) => response.result ?? [],
    }),
    moduleForms: builder.query<ModuleForms, ModuleApiProps>({
      query: (args: ModuleApiProps) =>
        `/modules/simple/${args.moduleId}?${createFieldFilterText({ filters: args.filters, isFirstParam: true })}`,
      transformResponse: (response: ModuleForms) => transformModuleForms(response),
      providesTags: (_result, _error, { moduleId }) => [{ type: 'ModuleForm', id: moduleId }],
    }),
    modulesInModuleGroups: builder.query<Array<Module>, void>({
      query: () => `/module-groups/simple`,
      transformResponse: (moduleGroups: ModuleGroups) =>
        moduleGroups.items
          .sort((moduleGroup1, moduleGroup2) => moduleGroup1.position - moduleGroup2.position)
          .map((moduleGroup) => {
            moduleGroup.modules?.sort((module1, module2) => module1.position - module2.position);
            return moduleGroup;
          })
          .map((moduleGroup) => moduleGroup.modules || [])
          .flat(),
    }),
    formPermissions: builder.query<FormPermissionsResults, number>({
      keepUnusedDataFor: 300, // 5 minutes
      query: (formId) => `/forms/${formId}/permissions`,
      transformResponse: (response: FormPermissionsResponse) => {
        return response.result;
      },
    }),
    formSettings: builder.query<ExtendedDynamicFormSettings, number>({
      query: (formId: number) => `/forms/${formId}`,
      transformResponse: (response: DynamicFormSettingsWrapper) => {
        return settingsToDynamicForm(response.result);
      },
    }),
    formAccessControl: builder.query<FormAccessControlResult, number>({
      query: (formId: number) => `/forms/${formId}/access-control`,
      transformResponse: (response: FormAccessControlResponse) => response.result,
    }),
    getFormById: builder.query<DynamicFormSettings, number>({
      query: (formId: number) => `/forms/${formId}`,
      providesTags: (result, error, arg) => [{ type: 'FormConfig', id: arg }],
      transformResponse: (response: DynamicFormSettingsWrapper) => response.result,
    }),
    putFormById: builder.mutation<void, DynamicFormSettingsWithOptionalId>({
      query: (form) => ({
        url: `/forms/${form.id}`,
        method: 'PUT',
        body: form,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'FormConfig', id: arg.id }, 'ModuleForm'],
    }),
    postFormByReferenceId: builder.mutation<number, PostFormByReferenceIdApiRequest>({
      query: (args) => ({
        url: `/forms/${args.referenceFormId}`,
        method: 'POST',
        body: args.form,
      }),
      transformResponse: (response: PostFormByReferenceIdApiResponse) => response.result,
      invalidatesTags: (_result, error, args) => (error ? [] : [{ type: 'ModuleForm', id: args.moduleFormId }]),
    }),
    getLocales: builder.query<Array<LocaleItem>, void>({
      query: () => `/locales`,
      transformResponse: (response: LocalesResponse) => response?.items || [],
    }),
    getTheme: builder.query<Record<string, string>, void>({
      query: () => `/theme`,
      providesTags: ['Theme'],
    }),
    updateTheme: builder.mutation<void, string>({
      query: (theme) => ({
        url: `/theme?companyTheme=${theme}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Theme'],
    }),
    getNews: builder.query<Array<NewsEntryProps>, NewsApiProps>({
      query: ({ dismissed, type, markAsSeen, publishState }: NewsApiProps) => {
        let newsUrl = '/news';
        if (dismissed !== undefined) {
          newsUrl += `?filter=dismissed:eq:${dismissed}`;
        }
        if (type) {
          newsUrl += `&filter=type:eq:${type}`;
        }
        if (markAsSeen !== undefined) {
          newsUrl += `&markAsSeen=${markAsSeen}`;
        }
        if (publishState) {
          newsUrl += `&filter=publishState:eq:${publishState}`;
        }
        return newsUrl;
      },
      transformResponse: (response: NewsResult) => {
        return transformNewsEntries(response);
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ impressionId, id }) => ({ type: 'News' as const, id: impressionId ?? id })),
              { type: 'News', id: 'LIST' },
            ]
          : [{ type: 'News', id: 'LIST' }],
    }),
    getWorkflowSteps: builder.query<Array<ExtendedDynamicFormWorkflowStep>, FormWorkflowStepsApiProps>({
      query: ({ formId, filters }) =>
        `/forms/${formId}/workflow-steps${createFieldFilterTextAsFirstQueryParameter(filters, true)}`,
      transformResponse: (response: GetWorkflowStepsResponse) => {
        return processWorkflowSteps(response.result);
      },
    }),
    getLicences: builder.query<LicencesInfo, void>({
      query: () => '/licences',
      transformResponse: (response: LicencesWrapper) => {
        return response.result;
      },
      providesTags: ['Licenses'],
    }),
    updateLicences: builder.mutation<void, Partial<LicencesInfo>>({
      query: (args) => ({
        url: '/licences',
        method: 'PATCH',
        body: args,
      }),
      invalidatesTags: ['Licenses'],
    }),
    getHomePageSummary: builder.query<HomePageSummary, { ignoreUserHierarchyRestrictions?: boolean }>({
      query: (args) => {
        let homePageUrl = '/homepage/summary';
        if (args.ignoreUserHierarchyRestrictions) {
          homePageUrl += '?ignoreUserHierarchyRestrictions=true';
        }
        return homePageUrl;
      },
      transformResponse: (response: HomePageSummaryResponse) => {
        return response.result;
      },
    }),
    getNavigationItems: builder.query<Array<NavigationItem>, void>({
      query: () => '/navigation-items',
      providesTags: ['NavigationItems'],
      transformResponse: (response: NavigationItemsApiResponse) => response.result ?? [],
    }),
    getSchemaDetails: builder.query<Array<SchemaDetails>, void>({
      query: () => '/schemas/details',
      transformResponse: (response: SchemaDetailsResponse) => {
        return response.result ?? [];
      },
    }),
    getCurrentSchemaInfo: builder.query<SchemaInfo, void>({
      query: () => '/schemas/current',
    }),
    updateNewsItemDismissed: builder.mutation<number, NewsItemDismissed>({
      query: ({ id, dismissed }) => ({
        url: `/news/news-item-impression/${id}`,
        method: 'PUT',
        body: { dismissed },
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'News', id: arg.id }],
    }),
    updateNewsItemSeen: builder.mutation<number, NewsItemSeen>({
      query: (args) => ({
        url: `/news/news-item-impression/`,
        method: 'POST',
        body: args,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'News', id: arg.newsItemId }],
    }),
    getWorkplaceForms: builder.query<Array<DynamicFormSettings>, void>({
      query: () => '/visitor-forms/simple',
      transformResponse: (response: WorkplaceSignInFormResponse) => response.result,
    }),
    getMetaData: builder.query<Array<MetaDataResponse>, void>({
      query: () => '/metadata/simple',
      transformResponse: (response: ResultValidationResponse<Array<MetaDataResponse>>) => response.result ?? [],
    }),
    getExternalUserConfiguration: builder.query<ExternalUserConfigResponse, void>({
      query: () => '/metadata/external-user-signup-configuration',
      transformResponse: (response: ResultValidationResponse<ExternalUserConfigResponse>) => response.result,
    }),
    getFeatureTours: builder.query<FeatureTours, void>({
      query: () => '/feature-tours',
      providesTags: ['FeatureTours'],
      transformResponse: (response: FeatureTourResponse) => response.result ?? {},
    }),
    updateFeatureTours: builder.mutation<void, FeatureTours>({
      query: (args) => ({ url: '/feature-tours', method: 'POST', body: args }),
      invalidatesTags: ['FeatureTours'],
    }),
    terminateDashboardSession: builder.mutation<void, string>({
      query: (sessionId) => ({ url: '/dashboard/terminate-session', method: 'POST', body: { sessionId } }),
    }),
    getHelpMetadata: builder.query<HelpMetadata, void>({
      query: () => '/metadata/help',
      transformResponse: (response: ResultValidationResponse<HelpMetadata>) => response.result,
      providesTags: ['HelpMetadata'],
    }),
    updateHelpMetadata: builder.mutation<void, HelpMetadata>({
      query: (body) => ({
        url: `/metadata/help`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['HelpMetadata'],
    }),
  }),
});

export const {
  useModuleWorkflowStepsQuery,
  useLazyModuleWorkflowStepsQuery,
  useModuleFormsQuery,
  useModulesInModuleGroupsQuery,
  useLazyModulesInModuleGroupsQuery,
  useFormPermissionsQuery,
  useFormSettingsQuery,
  useLazyFormSettingsQuery,
  useFormAccessControlQuery,
  useGetThemeQuery,
  useUpdateThemeMutation,
  useUpdateNewsItemSeenMutation,
  useUpdateNewsItemDismissedMutation,
  useGetNewsQuery,
  useGetWorkflowStepsQuery,
  useGetLicencesQuery,
  useGetNavigationItemsQuery,
  useGetHomePageSummaryQuery,
  useLazyGetSchemaDetailsQuery,
  useGetCurrentSchemaInfoQuery,
  useUpdateLicencesMutation,
  useGetWorkplaceFormsQuery,
  useGetMetaDataQuery,
  useGetExternalUserConfigurationQuery,
  useGetFeatureToursQuery,
  useUpdateFeatureToursMutation,
  useLazyFormPermissionsQuery,
  useGetFormByIdQuery,
  useLazyGetFormByIdQuery,
  usePutFormByIdMutation,
  useGetLocalesQuery,
  usePostFormByReferenceIdMutation,
  useTerminateDashboardSessionMutation,
  useGetHelpMetadataQuery,
  useUpdateHelpMetadataMutation,
} = api;
